'use client'

import {
  useDelayGroup,
  useDelayGroupContext,
  useId,
  useMergeRefs,
  useTransitionStyles
} from '@floating-ui/react'
import { FC, HTMLAttributes, Ref } from 'react'

import { cn } from '../../utils/cn'
import { useTooltipContext } from './tooltip'

interface TooltipContentProps extends HTMLAttributes<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>
}

export const TooltipContent: FC<TooltipContentProps> = ({
  className,
  style,
  children,
  ref,
  ...props
}) => {
  const { context, refs, getFloatingProps, strategy, x, y } =
    useTooltipContext()
  const resultRef = useMergeRefs([refs.setFloating, ref])

  const id = useId()

  const { isInstantPhase, currentId } = useDelayGroupContext()

  useDelayGroup(context, {
    id
  })

  const { isMounted, styles } = useTransitionStyles(context, {
    duration: isInstantPhase
      ? { open: 100, close: id === currentId ? 150 : 50 }
      : { open: 300, close: 200 },
    initial: {
      opacity: 0,
      transform: 'scale(0.8)'
    }
  })

  if (!isMounted) return null

  return (
    <div
      {...getFloatingProps({
        ...props,
        ref: resultRef
      })}
      className={cn(
        'z-5 rounded-md bg-slate-800 text-sm text-white shadow-md',
        'cursor-default rounded-md px-3 py-1.5',
        className
      )}
      style={{
        position: strategy,
        top: Math.round(y ?? 0),
        left: Math.round(x ?? 0),
        width: 'max-content',
        maxWidth: 'min(calc(100vw - 10px), 25rem)',
        ...style,
        ...styles
      }}>
      {children}
    </div>
  )
}
